
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TradeTransactionReason enum
 */
class TradeTransactionReason {
  /**
   * Initialize enum with a given value
   * @param {TradeTransactionReason|number|Int64|UInt64=} value Enum value, defaults is 0
   * @constructor
   */
  constructor (value = 0) {
    if (value instanceof TradeTransactionReason) {
      this.value = value.value
    } else {
      this.value = value
    }
  }

  /**
   * Is this enum equal to other one?
   * @this {!TradeTransactionReason}
   * @param {!TradeTransactionReason} other Other enum
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradeTransactionReason)) {
      throw new TypeError('Instance of TradeTransactionReason is required!')
    }
    return this.value === other.value
  }

  /**
   * Get enum value
   * @this {!TradeTransactionReason}
   * @returns {!number|!Int64|!UInt64} Enum value
   */
  valueOf () {
    return this.value
  }

  /**
   * Convert enum to string
   * @this {!TradeTransactionReason}
   * @returns {!string} Enum value string
   */
  toString () {
    if (this.value === TradeTransactionReason.UNKNOWN.value) {
      return 'UNKNOWN'
    }
    if (this.value === TradeTransactionReason.CLIENT_REQUEST.value) {
      return 'CLIENT_REQUEST'
    }
    if (this.value === TradeTransactionReason.MANAGER_REQUEST.value) {
      return 'MANAGER_REQUEST'
    }
    if (this.value === TradeTransactionReason.EXPIRED.value) {
      return 'EXPIRED'
    }
    if (this.value === TradeTransactionReason.NOT_ENOUGH_MARGIN.value) {
      return 'NOT_ENOUGH_MARGIN'
    }
    if (this.value === TradeTransactionReason.MARGIN_NOT_CALCULATED.value) {
      return 'MARGIN_NOT_CALCULATED'
    }
    if (this.value === TradeTransactionReason.PROFIT_NOT_CALCULATED.value) {
      return 'PROFIT_NOT_CALCULATED'
    }
    if (this.value === TradeTransactionReason.STOP_LOSS.value) {
      return 'STOP_LOSS'
    }
    if (this.value === TradeTransactionReason.TAKE_PROFIT.value) {
      return 'TAKE_PROFIT'
    }
    if (this.value === TradeTransactionReason.STOP_OUT.value) {
      return 'STOP_OUT'
    }
    if (this.value === TradeTransactionReason.COMMISSION_NOT_CALCULATED.value) {
      return 'COMMISSION_NOT_CALCULATED'
    }
    if (this.value === TradeTransactionReason.SOCIAL_TRADING.value) {
      return 'SOCIAL_TRADING'
    }
    return '<unknown>'
  }

  /**
   * Inspect enum
   * @this {!TradeTransactionReason}
   * @returns {!string} Enum value string
   */
  [util.inspect.custom] () {
    return this.toString()
  }

  /**
   * Convert enum to JSON
   * @this {!TradeTransactionReason}
   * @returns {!number} Enum value for JSON
   */
  toJSON () {
    return this.value
  }

  /**
   * Create enum from object value
   * @param {!number} other Object value
   * @returns {!TradeTransactionReason} Created enum
   */
  static fromObject (other) {
    return new TradeTransactionReason(other)
  }
}

// noinspection PointlessArithmeticExpressionJS
TradeTransactionReason.UNKNOWN = new TradeTransactionReason(0 + 0)
// noinspection PointlessArithmeticExpressionJS
TradeTransactionReason.CLIENT_REQUEST = new TradeTransactionReason(0 + 1)
// noinspection PointlessArithmeticExpressionJS
TradeTransactionReason.MANAGER_REQUEST = new TradeTransactionReason(0 + 2)
// noinspection PointlessArithmeticExpressionJS
TradeTransactionReason.EXPIRED = new TradeTransactionReason(0 + 3)
// noinspection PointlessArithmeticExpressionJS
TradeTransactionReason.NOT_ENOUGH_MARGIN = new TradeTransactionReason(0 + 4)
// noinspection PointlessArithmeticExpressionJS
TradeTransactionReason.MARGIN_NOT_CALCULATED = new TradeTransactionReason(0 + 5)
// noinspection PointlessArithmeticExpressionJS
TradeTransactionReason.PROFIT_NOT_CALCULATED = new TradeTransactionReason(0 + 6)
// noinspection PointlessArithmeticExpressionJS
TradeTransactionReason.STOP_LOSS = new TradeTransactionReason(0 + 7)
// noinspection PointlessArithmeticExpressionJS
TradeTransactionReason.TAKE_PROFIT = new TradeTransactionReason(0 + 8)
// noinspection PointlessArithmeticExpressionJS
TradeTransactionReason.STOP_OUT = new TradeTransactionReason(0 + 9)
// noinspection PointlessArithmeticExpressionJS
TradeTransactionReason.COMMISSION_NOT_CALCULATED = new TradeTransactionReason(0 + 10)
// noinspection PointlessArithmeticExpressionJS
TradeTransactionReason.SOCIAL_TRADING = new TradeTransactionReason(0 + 11)

export { TradeTransactionReason };

/**
 * Fast Binary Encoding TradeTransactionReason field model
 */
class FieldModelTradeTransactionReason extends fbe.FieldModel {
  /**
   * Get the field size
   * @this {!FieldModelTradeTransactionReason}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 1
  }

  /**
   * Get the value
   * @this {!FieldModelTradeTransactionReason}
   * @param {TradeTransactionReason=} defaults Default value, defaults is new TradeTransactionReason()
   * @returns {!TradeTransactionReason} Result value
   */
  get (defaults = new TradeTransactionReason()) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return defaults
    }

    return new TradeTransactionReason(this.readByte(this.fbeOffset))
  }

  /**
   * Set the value
   * @this {!FieldModelTradeTransactionReason}
   * @param {!TradeTransactionReason} value Value
   */
  set (value) {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return
    }

    this.writeByte(this.fbeOffset, value.value)
  }
}

export { FieldModelTradeTransactionReason };
